import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import {
  Users,
  Package,
  Settings,
  Building2,
  ShoppingCart,
  Truck,
  Bell,
  BarChart2,
  Map,
  Plug,
  FileText,
} from 'lucide-react';
import { useCurrentUser } from '../../features/auth/api';
import { usePriceSheetVisible } from '../../features/price-sheet/api';
import { useMyApplication } from '../../features/customer-application/api';
import { useAuth } from '../../auth/AuthProvider';

const ORDERS_URL_STORAGE_KEY = 'org-orders-last-url';
const PRODUCTS_URL_STORAGE_KEY = 'products-last-url';

type UserType = 'ORG_OWNER' | 'ORG_ADMIN' | 'ORG_USER' | 'USER' | 'SUBUSER';

type NavItem = {
  title: string;
  url: string;
  icon: any;
  description: string;
  disabled?: boolean;
  badgeCount?: number;
  badgeTone?: 'default' | 'attention';
};

export function useAppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();

  const { data: apiUser, isLoading: userLoading } = useCurrentUser();

  const { user: fbUser } = useAuth() as {
    user: {
      displayName?: string;
      email?: string;
    } | null;
  };

  const [ordersUrl, setOrdersUrl] = useState(() => {
    if (typeof window === 'undefined') {
      return '/orders';
    }

    return window.localStorage.getItem(ORDERS_URL_STORAGE_KEY) || '/orders';
  });

  useEffect(() => {
    if (location.pathname !== '/orders') {
      return;
    }

    const currentOrdersUrl = `${location.pathname}${location.search}`;

    window.localStorage.setItem(ORDERS_URL_STORAGE_KEY, currentOrdersUrl);

    setOrdersUrl(currentOrdersUrl);
  }, [location.pathname, location.search]);

  const [productsUrl, setProductsUrl] = useState(() => {
    if (typeof window === 'undefined') {
      return '/products';
    }

    return window.localStorage.getItem(PRODUCTS_URL_STORAGE_KEY) || '/products';
  });

  // Remember the products list's search/filters so the sidebar link returns to it.
  useEffect(() => {
    if (location.pathname !== '/products') {
      return;
    }

    const currentProductsUrl = `${location.pathname}${location.search}`;

    window.localStorage.setItem(PRODUCTS_URL_STORAGE_KEY, currentProductsUrl);

    setProductsUrl(currentProductsUrl);
  }, [location.pathname, location.search]);

  // Parse user data
  const apiUserObj = (apiUser as any)?.user ?? apiUser ?? null;

  const userType: UserType = (apiUserObj?.type as UserType) || 'USER';

  // The Price Sheet tab only shows for customers whose org has pricing on and
  // who are opted in — probed cheaply, and only queried for customer accounts.
  const isCustomerAccount = userType === 'USER' || userType === 'SUBUSER';
  const { data: priceSheetVisible } = usePriceSheetVisible(isCustomerAccount);

  // A primary customer who applied online and isn't approved yet needs a way back
  // to their application (to edit/resubmit when the rep asks for more info).
  const { data: myAppData } = useMyApplication(userType === 'USER');
  const myApplication = myAppData?.application;
  const hasOpenApplication =
    !!myApplication &&
    myApplication.status !== 'APPROVED' &&
    (myApplication.customerStatus ?? myApplication.status) !== 'APPROVED';
  const applicationNeedsInfo = myApplication?.status === 'NEEDS_INFO';

  const hasFinishedOrgSetup =
    apiUserObj?.organization && apiUserObj?.organization?.locations?.length > 0;

  const unseenOrderNotesCount = (apiUser as any)?.unseenOrderNotesCount ?? 0;

  const pendingOrderItemCount = (apiUser as any)?.pendingOrderItemCount ?? 0;

  const pendingApplicationCount = (apiUser as any)?.pendingApplicationCount ?? 0;

  const ORG_NAV: NavItem[] = [
    {
      title: 'Orders',
      url: ordersUrl,
      icon: ShoppingCart,
      description: 'Order management',
      disabled: !hasFinishedOrgSetup,
      badgeCount: pendingOrderItemCount,
      badgeTone: 'attention',
    },
    {
      title: 'Organization',
      url: '/organization',
      icon: Building2,
      description: 'Manage organization',
      disabled: false,
    },
    {
      title: 'Map',
      url: '/map',
      icon: Map,
      description: 'Customer locations and warehouses',
      disabled: !hasFinishedOrgSetup,
    },
    {
      title: 'Dashboard',
      url: '/dashboard',
      icon: BarChart2,
      description: 'Analytics & insights',
      disabled: !hasFinishedOrgSetup,
    },
    {
      title: 'Customers',
      url: '/customers',
      icon: Users,
      description: 'Customer management',
      disabled: !hasFinishedOrgSetup,
      badgeCount: pendingApplicationCount,
      badgeTone: 'attention',
    },
    {
      title: 'Carriers',
      url: '/carriers',
      icon: Truck,
      description: 'Carrier management',
      disabled: !hasFinishedOrgSetup,
    },
    {
      title: 'Products',
      url: productsUrl,
      icon: Package,
      description: 'Product catalog',
      disabled: !hasFinishedOrgSetup,
    },
    {
      title: 'Note Inbox',
      url: '/order-notes',
      icon: Bell,
      description: 'Order notes',
      disabled: false,
      badgeCount: unseenOrderNotesCount,
    },
  ];

  if (userType === 'ORG_OWNER' || userType === 'ORG_ADMIN') {
    ORG_NAV.push({
      title: 'Integrations',
      url: '/integrations',
      icon: Plug,
      description: 'Connected applications',
      disabled: false,
    });
  }

  ORG_NAV.push({
    title: 'User Settings',
    url: '/settings',
    icon: Settings,
    description: 'System configuration',
    disabled: false,
  });

  const priceSheetNavItem: NavItem = {
    title: 'Price Sheet',
    url: '/price-sheet',
    icon: FileText,
    description: 'Your pricing',
    disabled: false,
  };

  const USER_NAV: NavItem[] = [
    ...(hasOpenApplication
      ? [
          {
            title: 'Application',
            url: '/apply/status',
            icon: FileText,
            description: 'Your credit application',
            disabled: false,
            badgeCount: applicationNeedsInfo ? 1 : 0,
            badgeTone: 'attention' as const,
          },
        ]
      : []),
    {
      title: 'Add Order',
      url: '/customer-products',
      icon: Package,
      description: 'Browse products',
      disabled: false,
    },
    ...(priceSheetVisible ? [priceSheetNavItem] : []),
    {
      title: 'Orders',
      url: '/customer-orders',
      icon: ShoppingCart,
      description: 'Your orders',
      disabled: false,
    },
    {
      title: 'Subusers',
      url: '/customer-subusers',
      icon: Users,
      description: 'Manage subusers',
      disabled: false,
    },
    {
      title: 'User Settings',
      url: '/settings',
      icon: Settings,
      description: 'Account settings',
      disabled: false,
    },
  ];

  const SUB_USER_NAV: NavItem[] = [
    {
      title: 'Add Order',
      url: '/customer-products',
      icon: Package,
      description: 'Browse products',
      disabled: false,
    },
    ...(priceSheetVisible ? [priceSheetNavItem] : []),
    {
      title: 'Orders',
      url: '/customer-orders',
      icon: ShoppingCart,
      description: 'Your orders',
      disabled: false,
    },
    {
      title: 'User Settings',
      url: '/settings',
      icon: Settings,
      description: 'Account settings',
      disabled: false,
    },
  ];

  let navigationItems;

  if ((['ORG_OWNER', 'ORG_ADMIN', 'ORG_USER'] as UserType[]).includes(userType)) {
    navigationItems = ORG_NAV;
  } else if (userType === 'USER') {
    navigationItems = USER_NAV;
  } else {
    navigationItems = SUB_USER_NAV;
  }

  // Display names
  const displayName =
    apiUserObj?.name ||
    apiUserObj?.displayName ||
    fbUser?.displayName ||
    apiUserObj?.email ||
    fbUser?.email ||
    '—';

  const displayEmail = apiUserObj?.email || fbUser?.email || '—';

  // Route checking
  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  // Sign out handler
  const handleSignOut = async () => {
    try {
      await signOut(getAuth());
    } catch (_) {
      // no-op
    } finally {
      navigate('/login', { replace: true });
    }
  };

  return {
    navigationItems,
    displayName,
    displayEmail,
    userLoading,
    isActive,
    handleSignOut,
    userType,
  };
}
